import { useState } from "react";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, Upload } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { CaseFormData } from "../CaseForm";

interface FileUploadProps {
  form: UseFormReturn<CaseFormData>;
}

export function FileUpload({ form }: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const { toast } = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setIsProcessingFile(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Bruker ikke autentisert');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', user.id);

      const { data, error } = await supabase.functions.invoke('process-case-attachment', {
        body: formData,
      });

      if (error) throw error;
      
      if (data.success && data.analysis) {
        form.setValue('description', 
          (form.getValues('description') || '') + 
          '\n\nDokumentanalyse:\n' + data.analysis
        );
        
        toast({
          title: "Dokument analysert",
          description: "Dokumentet er lastet opp og analysert. AI-analysen er lagt til i beskrivelsen.",
        });
      }
    } catch (error) {
      console.error('Feil ved filbehandling:', error);
      toast({
        variant: "destructive",
        title: "Feil ved filbehandling",
        description: "Kunne ikke behandle filen. Vennligst prøv igjen.",
      });
    } finally {
      setIsProcessingFile(false);
    }
  };

  return (
    <div className="space-y-2">
      <FormLabel className="text-sm text-muted-foreground">
        Last opp dokument (valgfritt)
      </FormLabel>
      <div className="border-2 border-dashed rounded-lg p-4 hover:bg-accent/50 transition-colors">
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-8 w-8 text-muted-foreground" />
          <Input
            type="file"
            accept=".pdf,.doc,.docx,.txt,image/*"
            onChange={handleFileChange}
            disabled={isProcessingFile}
            className="cursor-pointer"
          />
          <p className="text-xs text-muted-foreground text-center">
            Støttede formater: PDF, Word, Text, og bilder<br />
            AI-analyse er tilgjengelig for alle filtyper
          </p>
        </div>
      </div>
      {isProcessingFile && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analyserer dokument med AI...
        </div>
      )}
      {selectedFile && !isProcessingFile && (
        <p className="text-sm text-muted-foreground">
          Valgt fil: {selectedFile.name}
        </p>
      )}
    </div>
  );
}